import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signOut
} from "firebase/auth";

import { auth, emailLinkActionCodeSettings } from "../firebase";
import { useMode } from "../context/ModeContext";
import AuthRedirect from "../components/AuthRedirect";
import uiStyles from "../theme/uiStyles";

const EMAIL_LINK_STORAGE_KEY = "nextbiteEmailForSignIn";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);
  const nav = useNavigate();
  const location = useLocation();

  const { setMode } = useMode();

  const getAuthErrorMessage = (error) => {
    switch (error?.code) {
      case "auth/unauthorized-continue-uri": {
        const redirectDomain = new URL(emailLinkActionCodeSettings.url).hostname;
        return `Firebase is blocking the sign-in link redirect. Add ${redirectDomain} to Firebase Auth > Settings > Authorized domains.`;
      }
      case "auth/invalid-credential":
        return "That email or password is incorrect.";
      case "auth/invalid-email":
        return "Enter a valid email address.";
      case "auth/user-disabled":
        return "This account has been disabled.";
      case "auth/too-many-requests":
        return "Too many attempts. Please wait a bit and try again.";
      default:
        return error?.message || "Unable to sign in right now.";
    }
  };

  const login = async () => {
    setErrorMessage("");
    setStatusMessage("");

    try {
      const { setPersistence, browserLocalPersistence, browserSessionPersistence } = await import("firebase/auth");
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );

      if (!userCredential.user.emailVerified) {
        await signOut(auth);
        setErrorMessage("Verify your email before logging in.");
        return;
      }

      setMode("personal");
      nav("/home");
    } catch (error) {
      setErrorMessage(getAuthErrorMessage(error));
    }
  };

  const sendMagicLink = async () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setErrorMessage("Enter your email first.");
      setStatusMessage("");
      return;
    }

    setLinkLoading(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      await sendSignInLinkToEmail(
        auth,
        trimmedEmail,
        emailLinkActionCodeSettings
      );

      window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, trimmedEmail);
      setStatusMessage(`Sign-in link sent to ${trimmedEmail}. Check your inbox.`);
    } catch (error) {
      setErrorMessage(getAuthErrorMessage(error));
    } finally {
      setLinkLoading(false);
    }
  };

  const forgotPassword = async () => {
    if (!email.trim()) {
      setErrorMessage("Enter email first.");
      setStatusMessage("");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email.trim());
      setStatusMessage("Password reset email sent.");
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(getAuthErrorMessage(error));
      setStatusMessage("");
    }
  };
  return (
    <AuthRedirect>
      <div style={uiStyles.navbar}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="/logo.png" alt="NextBite" style={{ height: 32, width: 32, objectFit: "contain" }} />
          <span style={{ fontWeight: 700, fontSize: 17, color: "#1a1a1a", letterSpacing: 0.2 }}>NextBite</span>
        </div>
        <button style={uiStyles.secondaryBtn} onClick={() => nav("/home")}>
          Home
        </button>
      </div>
      <div style={uiStyles.page}>
        <div style={{ ...uiStyles.card, padding: "28px 20px", display: "flex", flexDirection: "column", gap: "16px" }}>
          <h2 style={{ textAlign: "center", margin: 0 }}>Login</h2>

          {(location.state?.message || statusMessage) && (
            <p style={{ margin: 0, color: "#166534", fontSize: 14, textAlign: "center" }}>
              {location.state?.message || statusMessage}
            </p>
          )}

          {errorMessage && (
            <p style={{ margin: 0, color: "#dc2626", fontSize: 14, textAlign: "center" }}>
              {errorMessage}
            </p>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <input
              style={{ ...uiStyles.input, marginBottom: 0 }}
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              style={{ ...uiStyles.input, marginBottom: 0 }}
              placeholder="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: 14, color: "#374151" }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: "#f25f4c", cursor: "pointer" }}
              />
              Remember me
            </label>
            <span
              onClick={forgotPassword}
              style={{ cursor: "pointer", fontSize: 14, color: "#f25f4c", fontWeight: 500 }}
            >
              Forgot password?
            </span>
          </div>

          <button style={uiStyles.primaryBtn} onClick={login}>
            Login
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ flex: 1, height: 1, background: "#e7d8d3" }} />
            <span style={{ fontSize: 13, color: "#9ca3af" }}>or</span>
            <div style={{ flex: 1, height: 1, background: "#e7d8d3" }} />
          </div>

          <button
            style={{ ...uiStyles.secondaryBtn, width: "100%", textAlign: "center" }}
            onClick={sendMagicLink}
            disabled={linkLoading}
          >
            {linkLoading ? "Sending link..." : "Email me a sign-in link"}
          </button>

          <p style={{ margin: 0, textAlign: "center", fontSize: 14, color: "#6b7280" }}>
            Don't have an account?{" "}
            <span
              onClick={() => nav("/register")}
              style={{ cursor: "pointer", color: "#f25f4c", fontWeight: 600, textDecoration: "none" }}
            >
              Register
            </span>
          </p>
        </div>
      </div>
    </AuthRedirect>
  );
}
